/**
 * Pixel-space measurement utilities for Plan Takeoff.
 *
 * All measurements operate on pixel coordinates (not geographic lat/lng).
 * Conversion to real-world units requires a calibrated pixelsPerFoot value
 * derived from the calibration tool.
 *
 * pixelPaths entries are objects with { x, y } pixel coordinates.
 */

/**
 * Compute polygon area in square feet using the Shoelace formula.
 * Returns pixel area when pixelsPerFoot is 0 (uncalibrated).
 *
 * @param {Array<{x: number, y: number}>} pixelPaths - Closed polygon vertices in pixel space.
 * @param {number} pixelsPerFoot - Scale factor from calibration. 0 = uncalibrated.
 * @returns {number} Area in square feet (or square pixels when uncalibrated).
 */
export const polygonAreaSF = (pixelPaths, pixelsPerFoot) => {
  if (!pixelPaths || pixelPaths.length < 3) return 0;

  // Shoelace formula for signed area in pixel space
  let area = 0;
  const n = pixelPaths.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += pixelPaths[i].x * pixelPaths[j].y;
    area -= pixelPaths[j].x * pixelPaths[i].y;
  }
  const pixelArea = Math.abs(area) / 2;

  if (!pixelsPerFoot || pixelsPerFoot === 0) {
    // Uncalibrated — return raw pixel area so callers can display it
    return pixelArea;
  }

  // Convert pixel area to square feet
  return pixelArea / (pixelsPerFoot * pixelsPerFoot);
};

/**
 * Compute polyline length in linear feet using Euclidean distance.
 * Returns pixel distance when pixelsPerFoot is 0 (uncalibrated).
 *
 * @param {Array<{x: number, y: number}>} pixelPaths - Ordered vertices in pixel space.
 * @param {number} pixelsPerFoot - Scale factor from calibration. 0 = uncalibrated.
 * @returns {number} Length in feet (or pixels when uncalibrated).
 */
export const polylineLengthFeet = (pixelPaths, pixelsPerFoot) => {
  if (!pixelPaths || pixelPaths.length < 2) return 0;

  let totalPixels = 0;
  for (let i = 1; i < pixelPaths.length; i++) {
    totalPixels += segmentLengthPixels(pixelPaths[i - 1], pixelPaths[i]);
  }

  if (!pixelsPerFoot || pixelsPerFoot === 0) {
    return totalPixels;
  }

  return totalPixels / pixelsPerFoot;
};

/**
 * Compute the length of a single segment in feet.
 * Returns pixel distance when pixelsPerFoot is 0 (uncalibrated).
 *
 * @param {{x: number, y: number}} p1
 * @param {{x: number, y: number}} p2
 * @param {number} pixelsPerFoot - Scale factor from calibration. 0 = uncalibrated.
 * @returns {number} Length in feet (or pixels when uncalibrated).
 */
export const segmentLengthFeet = (p1, p2, pixelsPerFoot) => {
  const px = segmentLengthPixels(p1, p2);
  if (!pixelsPerFoot || pixelsPerFoot === 0) return px;
  return px / pixelsPerFoot;
};

/**
 * Internal: Euclidean pixel distance between two points.
 * @param {{x: number, y: number}} p1
 * @param {{x: number, y: number}} p2
 * @returns {number}
 */
const segmentLengthPixels = (p1, p2) => {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Format a foot value for display.
 * Trims unnecessary decimal places (e.g., 47.0 → "47'", 123.5 → "123.5'").
 *
 * @param {number} ft - Value in feet.
 * @returns {string} Formatted string with foot mark, e.g. "47'" or "123.5'".
 */
export const formatFeet = (ft) => {
  if (ft === null || ft === undefined || isNaN(ft)) return "0'";
  const rounded = parseFloat(ft.toFixed(1));
  // Drop trailing .0
  const display = rounded % 1 === 0 ? String(Math.round(rounded)) : rounded.toFixed(1);
  return `${display}'`;
};
